// frontend/src/components/ProjectRowV2.jsx
import { memo } from 'react';
import { Badge, Box, Grid, ListItemButton } from '@mui/material';
import { getLocalDate } from '../utils/fn.js';
import DiscreteLineV2 from './DiscreteLineV2.jsx';
import { financingCode } from '../utils/financing.js';

/**
 * Строка проекта в списке.
 *
 * ЧТО ИЗМЕНИЛОСЬ ПРОТИВ ПРЕЖНЕЙ ВЕРСИИ:
 *
 * 1. Один макет вместо двух. Раньше ветвление по matchDownMd (useMediaQuery)
 *    дублировало разметку целиком, и ветки успели разойтись: подсветка
 *    по hash была только в мобильной. Теперь размеры колонок задаются
 *    брейкпоинтами MUI (xs / md) — это обычный CSS, он не требует
 *    перерисовки при изменении ширины окна.
 *
 * 2. memo. В списке из сотен строк перерисовка родителя иначе
 *    перерисовывает каждую строку. ⚠️ Работает, только если родитель
 *    передаёт goto и handleSwitchTurn через useCallback, иначе функции
 *    создаются заново на каждый рендер и memo бесполезен.
 */
const ProjectRowV2 = ({
  hash,
  userInfo,
  project,
  showStages,
  showCreatedAt,
  financingMarkerMode = 'inline',
  showMortgageUnderline = false,
  goto,
  palette,
  handleSwitchTurn,
  canSetStages = false,
}) => {
  const isDark = palette.mode === 'dark';
  const isHighlighted = `#${project?._id}` === hash;
  const finance = project?.financingType;
  const financeCode = financingCode(finance);
  const isMortgage = financeCode === 'mortgage';
  const markerText = financeCode === 'undecided' ? ''
    : finance?.shortName || ({ mortgage: 'И', installment: 'Р', full_payment: 'Д' })[financeCode] || '';

  // Закрытый проект подсвечивается фоном
  const closedBg = !project.visible ? (isDark ? '#3e2723' : '#ffebee') : null;

  // Цвет ипотечного маркера показывает наиболее поздний этап раскрытия эскроу.
  const escrowColor = project?.isEscrowDisclosed === true
    ? 'success'
    : project?.isEscrowDisclosureDocumentsSubmitted === true
      ? 'warning'
      : 'error';
  const markerColor = isMortgage ? escrowColor : financeCode === 'installment' ? 'primary' : 'default';
  const markerBg = isMortgage && escrowColor === 'warning' && !isDark
    ? '#FFEB3B'
    : markerColor === 'default'
      ? palette.grey[isDark ? 500 : 600]
      : palette[markerColor].main;
  const markerForeground = isMortgage && escrowColor === 'warning' && !isDark
    ? '#1A1A1A'
    : markerColor === 'default' ? '#fff' : palette[markerColor].contrastText;
  const badgeSx = isMortgage && escrowColor === 'warning' && !isDark
    ? { '& .MuiBadge-badge': { bgcolor: '#FFEB3B', color: '#1A1A1A' } }
    : financeCode === 'full_payment'
      ? { '& .MuiBadge-badge': { bgcolor: palette.grey[isDark ? 500 : 600], color: '#fff' } }
      : undefined;


  return (
    <Grid
      id={project?._id}
      container
      spacing={0}
      alignItems='center'
      sx={{
        bgcolor: closedBg,
        color: !project.visible ? palette.text.disabled : null,
        py: userInfo?.isAdmin ? 0 : 1,
      }}
    >
      {/* Заголовок и дата: на мобильном во всю ширину, с md — 5 колонок */}
      <Grid
        item
        xs={12}
        md={5}
        container
        sx={{
          // Тернарник вместо &&: при false в sx уходит false, а не undefined.
          // Сейчас работает, но undefined — то, что MUI ожидает
          borderBottom: showMortgageUnderline && isMortgage
            ? { xs: '3px solid red', md: '2px solid red' }
            : undefined,
        }}
      >
        <Grid
          item
          xs={9}
          sx={{
            // Раньше подсветка якоря была только в мобильной вёрстке
            color: isHighlighted ? (isDark ? '#f9ff00' : '#d91600') : null,
          }}
        >
          {financingMarkerMode === 'badge' ? (
            <Badge
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              color={markerColor}
              sx={badgeSx}
              invisible={!markerText}
              badgeContent={markerText}
            >
              <ListItemButton
                onClick={() => goto(project._id)}
                sx={{ ml: 0, pl: 0, pb: showStages ? 0 : 1 }}
              >
                <strong>{project.title}</strong>
              </ListItemButton>
            </Badge>
          ) : (
            <ListItemButton
              onClick={() => goto(project._id)}
              sx={{ ml: 0, pl: 0, pb: showStages ? 0 : 1, alignItems: 'flex-start', gap: markerText ? 0.5 : 0 }}
            >
              {markerText && (
                <Box
                  component='span'
                  role='img'
                  aria-label={`Финансирование: ${finance?.title || markerText}`}
                  title={finance?.title}
                  sx={{
                    display: 'inline-flex',
                    flex: '0 0 18px',
                    width: 18,
                    height: 18,
                    mt: '3px',
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: markerBg,
                    color: markerForeground,
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {markerText}
                </Box>
              )}
              <Box component='strong' sx={{ minWidth: 0 }}>{project.title}</Box>
            </ListItemButton>
          )}
        </Grid>

        <Grid
          item
          xs={3}
          alignItems='center'
          sx={{ fontSize: '11px', textAlign: 'end', pt: '10px', pr: { md: 0.5 } }}
        >
          {showCreatedAt ? getLocalDate(project?.createdAt) : project?.period}
        </Grid>
      </Grid>

      {/* Этапы. Раньше две почти одинаковые ветки различались тем,
          кликабелен ли блок: при показе этапов клики обрабатывает
          сама шкала, иначе клик ведёт в карточку проекта */}
      <Grid
        item
        xs={12}
        md={7}
        onClick={showStages ? undefined : () => goto(project._id)}
      >
        <DiscreteLineV2
          data={project?.stages}
          projectId={project._id}
          palette={palette}
          showStages={showStages}
          notitle={!showStages}
          handleSwitchTurn={showStages ? handleSwitchTurn : undefined}
          canSetStages={canSetStages}
        />
      </Grid>
    </Grid>
  );
};

export default memo(ProjectRowV2);
